import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import {
  getUserSubscription,
  subscribeToSubscriptionChanges,
  createCheckoutSession,
  createPortalSession,
  getProducts,
  Subscription,
  Product
} from '../services/firebaseStripeService';

interface StripeContextType {
  subscription: Subscription | null;
  hasAccess: boolean;
  products: Product[];
  isLoading: boolean;
  subscribe: (priceId: string) => Promise<void>;
  openPortal: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within StripeProvider');
  }
  return context;
};

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load products
  useEffect(() => {
    getProducts().then(setProducts).catch(console.error);
  }, []);

  // Subscribe to subscription changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setSubscription(null);
      setHasAccess(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    const unsubscribe = subscribeToSubscriptionChanges((sub) => {
      setSubscription(sub);
      setHasAccess(sub !== null && (sub.status === 'active' || sub.status === 'trialing'));
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, user]);

  const subscribe = async (priceId: string) => {
    try {
      const url = await createCheckoutSession(priceId);
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  };

  const openPortal = async () => {
    try {
      const url = await createPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  };

  const refreshSubscription = async () => {
    setIsLoading(true);
    try {
      const sub = await getUserSubscription();
      setSubscription(sub);
      setHasAccess(sub !== null && (sub.status === 'active' || sub.status === 'trialing'));
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StripeContext.Provider value={{
      subscription,
      hasAccess,
      products,
      isLoading,
      subscribe,
      openPortal,
      refreshSubscription
    }}>
      {children}
    </StripeContext.Provider>
  );
}; 